import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Link2, Copy, Check, Trash2, Plus, UserPlus, MousePointerClick, LogIn,
  Lightbulb, TrendingUp, Ban, RotateCcw, Mail, Send, ChevronDown, Eye,
} from "lucide-react";
import { api, apiErr, adminDelete } from "../../lib/api";
import PartnerFiche from "../../components/PartnerFiche";
import { SOCIAL_NETWORKS } from "../../lib/socials";

const TYPES = [
  { v: "blog", l: "Blog" },
  { v: "site", l: "Site web" },
  { v: "influenceur", l: "Influenceur / créateur" },
  { v: "autre", l: "Autre" },
];
const TYPE_LABELS = Object.fromEntries(TYPES.map((t) => [t.v, t.l]));
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Podium des partenaires (par nombre d'inscrits) pour motiver.
function PartnerPodium({ partners }) {
  const top = [...partners].sort((a, b) => (b.signups || 0) - (a.signups || 0)).slice(0, 3);
  if (!top.length || top.every((p) => !(p.signups > 0))) return null;
  const medals = [
    { label: "1er", cls: "border-amber-400/50 bg-amber-400/10 text-amber-300" },
    { label: "2e", cls: "border-slate-300/40 bg-slate-300/10 text-slate-200" },
    { label: "3e", cls: "border-amber-700/50 bg-amber-700/10 text-amber-500" },
  ];
  return (
    <div className="mb-4 grid gap-3 sm:grid-cols-3" data-testid="partner-podium">
      {top.map((p, i) => (
        <div key={p.id} className={`flex items-center gap-3 rounded-xl border p-3 ${medals[i].cls}`} data-testid={`podium-${i + 1}`}>
          <span className="font-display text-lg font-900">{medals[i].label}</span>
          {p.has_logo
            ? <img src={`${BACKEND}/api/partners/${p.id}/logo`} alt={p.name} className="h-9 w-9 rounded-lg object-cover" />
            : <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 font-display text-sm font-700">{p.name.slice(0, 2).toUpperCase()}</span>}
          <div className="min-w-0">
            <div className="truncate text-sm font-600 text-white">{p.name}</div>
            <div className="text-[11px] opacity-80">{p.signups} inscrit{p.signups > 1 ? "s" : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

const origin = () => window.location.origin;

export default function AdminLinks() {
  const [partners, setPartners] = useState([]);
  const [invites, setInvites] = useState([]);
  const [f, setF] = useState({ name: "", ptype: "blog", email: "", password: "", note: "" });
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [ficheFor, setFicheFor] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const TABS = [
    { key: "partenaires", label: "Partenaires", icon: TrendingUp, color: "cyan" },
    { key: "creer", label: "Créer un partenaire", icon: Plus, color: "green" },
    { key: "invitations", label: "Invitations", icon: Send, color: "amber" },
  ];
  // Couleurs distinctes par onglet (bien visibles même quand tout est fermé).
  const TAB_COLORS = {
    cyan: { active: "border-cyan bg-cyan/15 text-cyan", idle: "border-cyan/30 bg-cyan/5 text-cyan/80 hover:border-cyan hover:text-cyan" },
    green: { active: "border-green-400 bg-green-400/15 text-green-400", idle: "border-green-400/30 bg-green-400/5 text-green-400/80 hover:border-green-400 hover:text-green-400" },
    amber: { active: "border-amber-400 bg-amber-400/15 text-amber-300", idle: "border-amber-400/30 bg-amber-400/5 text-amber-300/80 hover:border-amber-400 hover:text-amber-300" },
  };

  const load = () => {
    api.get("/admin/partners").then((r) => setPartners(r.data)).catch(() => {});
    api.get("/admin/partner-invites").then((r) => setInvites(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const copyText = async (text, id) => {
    try { await navigator.clipboard.writeText(text); }
    catch { const t = document.createElement("textarea"); t.value = text; document.body.appendChild(t); t.select(); document.execCommand("copy"); t.remove(); }
    setCopiedId(id);
    toast.success("Lien copié.");
    setTimeout(() => setCopiedId(null), 1800);
  };

  const createPartner = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Indiquez le nom du partenaire.");
    if (!f.email.trim()) return toast.error("Indiquez l'e-mail du partenaire.");
    if (f.password.length < 6) return toast.error("Mot de passe trop court (min 6 caractères).");
    setCreating(true);
    try {
      await api.post("/admin/partners", { name: f.name.trim(), email: f.email.trim(), password: f.password, ptype: f.ptype, note: f.note.trim() });
      toast.success(`Partenaire « ${f.name.trim()} » créé — son compte et son lien sont prêts.`);
      setF({ name: "", ptype: "blog", email: "", password: "", note: "" });
      load();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setCreating(false); }
  };

  const genInvite = async () => {
    try {
      const { data } = await api.post("/admin/partner-invites");
      toast.success("Lien d'invitation partenaire généré.");
      copyText(`${origin()}/partenaire/inscription/${data.code}`, `inv-${data.id}`);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/admin/partners/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Lien désactivé (les clics ne sont plus comptés)." : "Lien réactivé.");
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire « ${p.name} » ? Son compte et ses statistiques seront supprimés.`)) return;
    try { if (!await adminDelete(`/admin/partners/${p.id}`)) return; toast.success("Partenaire supprimé."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  const removeInvite = async (id) => {
    try { if (!await adminDelete(`/admin/partner-invites/${id}`)) return; toast.success("Invitation supprimée."); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <div className="mx-auto max-w-6xl" data-testid="admin-links">
      <div className="text-[11px] uppercase tracking-[0.25em] text-cyan">Marketing · Attribution</div>
      <h1 className="mt-2 font-display text-3xl font-900 text-white">Liens marketing & Partenaires</h1>
      <p className="mt-2 max-w-2xl text-sm text-ink-secondary">
        Créez un lien tracké par partenaire (blog, site, influenceur). Chaque clic mène à l'inscription et est compté ;
        chaque inscription est rattachée au partenaire. Suivez clics, inscrits, connexions et idées par partenaire.
      </p>

      {/* Créer un partenaire */}
      {/* Section « Créer un partenaire » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-creer">Créer un partenaire</div>
      <div className="mt-8 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-create-card">
        <div className="flex items-center gap-2 text-sm font-600 text-white"><Plus size={16} className="text-cyan" /> Créer un partenaire (compte + lien immédiats)</div>
        <form onSubmit={createPartner} className="mt-4 grid gap-3 sm:grid-cols-2">
          <input value={f.name} onChange={set("name")} placeholder="Nom du partenaire (ex : Blog Tech du Web)" data-testid="partner-name"
            className="rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary focus:border-cyan focus:outline-none" />
          <select value={f.ptype} onChange={set("ptype")} data-testid="partner-type"
            className="rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white focus:border-cyan focus:outline-none">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <input value={f.email} onChange={set("email")} type="email" placeholder="E-mail de connexion du partenaire" data-testid="partner-email"
            className="rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary focus:border-cyan focus:outline-none" />
          <input value={f.password} onChange={set("password")} type="text" placeholder="Mot de passe (min 6)" data-testid="partner-password"
            className="rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary focus:border-cyan focus:outline-none" />
          <input value={f.note} onChange={set("note")} placeholder="Note (optionnel)" data-testid="partner-note"
            className="rounded-lg border border-white/15 bg-void px-4 py-2.5 text-sm text-white placeholder:text-ink-tertiary focus:border-cyan focus:outline-none sm:col-span-2" />
          <button type="submit" disabled={creating} data-testid="partner-create-submit"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan px-4 py-2.5 text-sm font-600 text-void transition-all hover:glow-cyan disabled:opacity-60 sm:col-span-2">
            {creating ? "Création…" : "Créer le partenaire"} <UserPlus size={15} />
          </button>
        </form>
      </div>
      </>

      {/* Inviter un partenaire */}
      {/* Section « Invitations » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-invitations">Invitations</div>
      <div className="mt-6 rounded-2xl border border-white/10 bg-surface p-5" data-testid="partner-invite-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-600 text-white"><Send size={16} className="text-cyan" /> Inviter un partenaire à s'inscrire lui-même</div>
            <p className="mt-1 text-xs text-ink-secondary">Générez un lien d'invitation : le partenaire crée son compte et reçoit son propre lien à diffuser à ses clients.</p>
          </div>
          <button onClick={genInvite} data-testid="partner-gen-invite"
            className="inline-flex items-center gap-2 rounded-lg border border-cyan/40 bg-cyan/10 px-4 py-2.5 text-sm font-600 text-cyan transition-all hover:bg-cyan/20">
            <Link2 size={15} /> Générer un lien d'invitation
          </button>
        </div>
        {invites.length > 0 && (
          <div className="mt-4 space-y-2">
            {invites.map((iv) => (
              <div key={iv.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-void px-3 py-2" data-testid={`invite-row-${iv.id}`}>
                <code className="min-w-0 flex-1 truncate font-mono text-xs text-cyan">{origin()}/partenaire/inscription/{iv.code}</code>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-600 uppercase ${iv.used_by_user_id ? "bg-green-500/15 text-green-400" : "bg-amber-400/15 text-amber-300"}`}>
                  {iv.used_by_user_id ? "Utilisé" : "Disponible"}
                </span>
                {!iv.used_by_user_id && (
                  <button onClick={() => copyText(`${origin()}/partenaire/inscription/${iv.code}`, `inv-${iv.id}`)} data-testid={`invite-copy-${iv.id}`}
                    className="inline-flex items-center gap-1 text-xs text-cyan hover:underline">
                    {copiedId === `inv-${iv.id}` ? <Check size={13} /> : <Copy size={13} />} Copier
                  </button>
                )}
                <button onClick={() => removeInvite(iv.id)} data-testid={`invite-delete-${iv.id}`} className="text-ink-tertiary transition-colors hover:text-red-400"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      </>

      {/* Liste des partenaires */}
      {/* Section « Partenaires » — affichée en permanence (accordéon supprimé) */}
      <>
        <div className="mt-8 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan" data-testid="section-title-partenaires">Partenaires</div>
      <div className="mt-8" data-testid="partner-list">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-600 text-white"><TrendingUp size={16} className="text-cyan" /> Partenaires ({partners.length})</div>
          <button onClick={() => document.querySelector('[data-testid="partner-create-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" })} data-testid="goto-create-partner"
            className="inline-flex items-center gap-1.5 rounded-lg bg-green-500 px-3.5 py-2 text-xs font-600 text-void transition-all hover:bg-green-400">
            <Plus size={14} /> Créer un partenaire
          </button>
        </div>
        <PartnerPodium partners={partners} />
        {partners.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-sm text-ink-tertiary">Aucun partenaire pour le moment.</div>
        ) : (
          <div className="space-y-4">
            {partners.map((p) => (
              <div key={p.id} className="rounded-2xl border border-white/10 bg-surface p-5" data-testid={`partner-card-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-display text-lg font-700 text-white">{p.name}</span>
                      <span className="rounded-full bg-electric/20 px-2 py-0.5 text-[10px] font-600 uppercase text-electric">{TYPE_LABELS[p.ptype] || p.ptype}</span>
                      {p.active
                        ? <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-green-400">Actif</span>
                        : <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-600 uppercase text-red-400">Inactif</span>}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-secondary">
                      <a href={`mailto:${p.contact_email}`} className="inline-flex items-center gap-1 underline-offset-2 hover:text-cyan hover:underline"><Mail size={12} /> {p.contact_email}</a>
                      {p.note && <span>· {p.note}</span>}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <code className="truncate rounded-lg border border-white/10 bg-void px-3 py-2 font-mono text-xs text-cyan" data-testid={`partner-link-${p.id}`}>{origin()}/r/{p.code}</code>
                      <button onClick={() => copyText(`${origin()}/r/${p.code}`, p.id)} data-testid={`partner-copy-${p.id}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-2 text-xs text-white transition-colors hover:border-cyan">
                        {copiedId === p.id ? <Check size={13} className="text-green-400" /> : <Copy size={13} />} {copiedId === p.id ? "Copié" : "Copier le lien"}
                      </button>
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button onClick={() => setFicheFor(p)} title="Ouvrir la fiche partenaire" data-testid={`partner-fiche-open-${p.id}`}
                      className="rounded-lg border border-white/15 p-2 text-white transition-colors hover:border-cyan"><Eye size={15} /></button>
                    <button onClick={() => toggleActive(p)} title={p.active ? "Désactiver le lien" : "Réactiver le lien"} data-testid={`partner-toggle-${p.id}`}
                      className="rounded-lg border border-white/15 p-2 text-white transition-colors hover:border-cyan">
                      {p.active ? <Ban size={15} /> : <RotateCcw size={15} />}
                    </button>
                    <button onClick={() => remove(p)} title="Supprimer" data-testid={`partner-delete-${p.id}`}
                      className="rounded-lg border border-white/15 p-2 text-ink-tertiary transition-colors hover:border-red-400 hover:text-red-400"><Trash2 size={15} /></button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <Stat icon={MousePointerClick} c="text-cyan" v={p.clicks} l="Clics" id={p.id} k="clicks" />
                  <Stat icon={UserPlus} c="text-green-400" v={p.signups} l="Inscrits" id={p.id} k="signups" />
                  <Stat icon={LogIn} c="text-electric" v={p.logins} l="Connectés" id={p.id} k="logins" />
                  <Stat icon={Lightbulb} c="text-amber-400" v={p.ideas} l="Idées" id={p.id} k="ideas" />
                  <div className="rounded-xl border border-white/10 bg-void p-3 text-center" data-testid={`partner-conv-${p.id}`}>
                    <div className="font-display text-xl font-900 text-cyan tabular-nums">{p.conversion} %</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-tertiary">Conversion</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </>
      {ficheFor && (
        <PartnerFiche partner={ficheFor} onClose={() => setFicheFor(null)} onSaved={() => { load(); }} />
      )}
    </div>
  );
}

function Stat({ icon: Icon, c, v, l, id, k }) {
  return (
    <div className="rounded-xl border border-white/10 bg-void p-3 text-center" data-testid={`partner-${k}-${id}`}>
      <Icon size={16} className={`mx-auto ${c}`} />
      <div className="mt-1.5 font-display text-xl font-900 text-white tabular-nums">{v}</div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-ink-tertiary">{l}</div>
    </div>
  );
}
